import React, { useCallback, useState } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import VisibilityIcon from "@mui/icons-material/Visibility";
import DownloadIcon from "@mui/icons-material/Download";
import { downloadFileModdules } from "../CashAllocation/constants";
import { fetchFile } from "../../Services/api";
import { toast } from "react-toastify";
import instance from "../../redux/api";
import { CircularProgress } from "@mui/material";

const CommonMultiFileUpload = ({
  isFileUploadDialogOpen,
  setIsFileUploadDialogOpen,
  uploadedFiles,
  setUploadedFiles,
  onFilesUploaded,
  currentWorkflowDetails,
}) => {

  const [formData, setFormData] = useState({
    document_details: "",
    file: null,
  });
  const [fileSizeError, setFileSizeError] = useState(false)

  const handleViewFile = useCallback((file) => {
    const defaultFileType = typeof(file)
    const url = URL.createObjectURL(file);
    
    // For images, PDFs, and text files, we can view them directly
    if (file.type.startsWith('image/') || file.type === 'application/pdf' || file.type.startsWith('text/')) {
      window.open(url, '_blank');
    } else {
      // For other file types, automatically download with same name
      defaultFileType === 'object' ? handleDownloadFile(file) : handleDownloadClick(file);
    }
  }, []);

  const handleDownloadFile = useCallback((file) => {
    const url = URL.createObjectURL(file);
    const link = document.createElement('a');
    link.href = url;
    link.download = file.name; // This preserves the original filename
    link.style.display = 'none';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }, []);

  const [downloadingFile, setDownloadingFile] = useState(null);
  const showToast = (type, message) => {
    toast[type](message, {
      position: toast.POSITION.TOP_RIGHT,
      autoClose: 5000,
    });
  };

  const handleDownloadClick = async (bucketKey, id) => {
    try {
      setDownloadingFile(id);
      const module = downloadFileModdules.workflow;
      const url = `${fetchFile}?key=${bucketKey}&module=${module}&moduleName=WF_Bank_Amount_Change`;
  
      const response = await instance.get(url, { responseType: "blob" });
      const contentType = response.headers["content-type"];
      const blob = new Blob([response.data], { type: contentType });
      const fileURL = window.URL.createObjectURL(blob);
  
      const link = document.createElement("a");
      link.href = fileURL;
      link.download = bucketKey || "download";
      document.body.appendChild(link);
      link.click();

      document.body.removeChild(link);
      window.URL.revokeObjectURL(fileURL);

      showToast("success", "File downloaded successfully.");
    } catch (error) {
      showToast("error", "Failed to download the file.");
    } finally {
      setDownloadingFile(null);
    }
  }

  const handleCloseDialog = useCallback(() => {
    setIsFileUploadDialogOpen(false);
    setFileSizeError(false);
    setFormData({
      document_details: "",
      file: null,
    });
  }, []);

  const handleMultipleFileSelect = useCallback((e) => {
    const files = Array.from(e.target.files);

    if (files.length > 0) {
      const newFiles = files.map(file => ({
        name: file.name,
        file: file,
      }));
      setUploadedFiles(prev => [...prev, ...newFiles]);
    }
  }, [setUploadedFiles]);

  return (
    <>
      <Dialog open={isFileUploadDialogOpen} onClose={handleCloseDialog} fullWidth>
          <DialogTitle style={{ background: "#FCFCFC" }}>Files</DialogTitle>
          <DialogContent>
            <Grid container>
              <TableContainer>
                {uploadedFiles.length > 0 && <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>File Name</TableCell>
                      <TableCell>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {uploadedFiles.map((file, index) => (
                    <TableRow key={index}>
                      <TableCell>{file.name ?? file}</TableCell>
                      <TableCell>
                        <IconButton 
                          size="small" 
                          color="primary"
                          onClick={() => typeof(file) === 'object' ? handleViewFile(file.file) : handleDownloadClick(file, index)}
                          title="View File"
                        >
                          <VisibilityIcon style={{ fontSize: 18, color: "#1976d2" }}/>
                        </IconButton>
                        {downloadingFile === index ? (
                          <CircularProgress
                            style={{ color: "rgb(255, 90, 1)" }}
                            size={22}
                          />
                        ) : (
                          <IconButton 
                            size="small" 
                            color="success"
                            onClick={() => typeof(file) === 'object' ? handleDownloadFile(file.file) : handleDownloadClick(file, index)}
                            title="Download File"
                          >
                            <DownloadIcon style={{ fontSize: 18, color: "#2e7d32" }}/>
                          </IconButton>
                        )}
                        {typeof(file) === 'object' && (
                          <IconButton 
                            size="small" 
                            color="error"
                            onClick={() => {
                            setUploadedFiles(prev => prev.filter((_, i) => i !== index));
                            }}
                            title="Delete File"
                          >
                            <DeleteIcon style={{ fontSize: 18, color: "#ff5a01" }}/>
                          </IconButton>
                        )}
                      </TableCell>
                    </TableRow>
                    ))}
                  </TableBody>
                </Table>}
              </TableContainer>
              {(uploadedFiles.length === 0 && currentWorkflowDetails && Object.keys(currentWorkflowDetails)?.length > 0 )? (
                <Grid item xs={12} marginTop={4}>
                  <Typography>No files uploaded</Typography>
                </Grid>
              ):(
                (typeof uploadedFiles[0] === "object" || !currentWorkflowDetails || Object.keys(currentWorkflowDetails)?.length === 0) && (
                  <Grid item xs={12} marginTop={4}>
                    <Button color="inherit" variant="outlined" component="label" sx={{
                      backgroundColor: "#F5F5F5",
                      color: "#000",
                      border: "1px solid #000",
                      borderRadius: "5px",
                      padding: "10px 20px",
                    }}>
                      Select Multiple Files
                      <input
                        type="file"
                        hidden
                        multiple
                        onChange={handleMultipleFileSelect}
                      />
                    </Button>
                  </Grid>
                )
              )}
            </Grid>
          </DialogContent>
          <DialogActions
            style={{ justifyContent: "flex-end", padding: "15px 25px" }}
          >
            <Button onClick={handleCloseDialog} color="inherit" variant="outlined">
              Close
            </Button>
          </DialogActions>
      </Dialog>
    </>
  );
};

export default CommonMultiFileUpload;